import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';

import type { TocItem } from './toc';

interface ArticleTocProps {
  toc: TocItem[];
}

/** Surligne la section actuellement visible (scroll-spy). */
function useActiveSection(ids: string[]): string {
  const [active, setActive] = useState<string>('');
  const key = ids.join(',');

  useEffect(() => {
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0] !== undefined) setActive(visible[0].target.id);
      },
      { rootMargin: '-15% 0px -75% 0px', threshold: 0 },
    );
    els.forEach((el) => { observer.observe(el); });
    return () => { observer.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return active;
}

/** Colonne de lecture : encadré « Pour aller plus loin » + sommaire de l'article. */
export default function ArticleToc({ toc }: ArticleTocProps): JSX.Element {
  const active = useActiveSection(toc.map((item) => item.id));

  return (
    <aside className="ArticleToc">
      <div className="ArticleFurther">
        <p className="ArticleFurther__label">Pour aller plus loin</p>
        <p className="ArticleFurther__text">
          Asterion Solutions — structurez votre qualité sans multiplier la paperasse.
        </p>
        <a
          className="ArticleFurther__cta"
          href="https://solutions-asterion.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          Découvrir Asterion <ArrowUpRight size={14} />
        </a>
      </div>

      {toc.length > 0 && (
        <nav className="ArticleToc__nav" aria-label="Dans cet article">
          <p className="ArticleToc__label">Dans cet article</p>
          <ul>
            {toc.map((item) => (
              <li key={item.id}>
                <a
                  className={`ArticleToc__link ${active === item.id ? 'is-active' : ''}`}
                  href={`#${item.id}`}
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </aside>
  );
}
